<template>
  <div class="min-h-screen flex flex-col bg-background text-foreground font-sans antialiased">
    <!-- The operator chrome: same shell grammar as the tenant header, but the
         « Administration » badge + amber accent line make god-mode unmistakable. -->
    <div class="h-1 w-full bg-amber-500 dark:bg-amber-600 shrink-0" />
    <header class="sticky top-0 z-40 w-full border-b bg-card shrink-0">
      <div class="flex h-14 items-center justify-between px-6">
        <div class="flex items-center gap-4">
          <router-link to="/" class="flex items-center gap-2 font-bold text-xl leading-none">
            <BrandMark />
          </router-link>
          <Badge variant="secondary" class="uppercase tracking-wider">{{ t('admin.title') }}</Badge>
          <!-- The page's name, same slot as the tenant header (PageTitle). -->
          <div :id="PAGE_TITLE_HOST_ID" class="flex min-w-0 items-center" />
        </div>

        <div class="flex items-center gap-2">
          <span v-if="auth.user" class="text-xs text-muted-foreground hidden sm:block">
            {{ auth.user.attributes.email }}
          </span>
          <Button variant="ghost" size="icon" @click="toggleTheme">
            <SunIcon v-if="isDark" class="h-[1.2rem] w-[1.2rem]" />
            <MoonIcon v-else class="h-[1.2rem] w-[1.2rem]" />
            <span class="sr-only">Toggle theme</span>
          </Button>
          <Button variant="ghost" size="icon" @click="handleLogout">
            <LogOutIcon class="h-[1.2rem] w-[1.2rem]" />
            <span class="sr-only">{{ t('auth.logout') }}</span>
          </Button>
        </div>
      </div>
    </header>

    <!-- Workspace below the chrome (amber 4px + h-14 header = 60px), the
         console OrganizationLayout's structure: rail and main each own their
         scroll. ⚠ `bg-background` on the fixed wrapper AND the scroller is
         load-bearing (see OrganizationLayout) — a compositing layer without a
         provably opaque background loses subpixel antialiasing. -->
    <div class="fixed inset-x-0 top-[60px] bottom-0 flex overflow-hidden bg-background">
      <aside class="flex w-60 flex-col border-r bg-card shrink-0 overflow-y-auto">
        <AdminSidebarNav class="flex-1 mt-4" />
      </aside>

      <!-- Bare scroller, the console standard: the layout owns no content
           framing — views bring their own PageContainer. -->
      <main class="flex-1 overflow-y-auto relative bg-background">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import BrandMark from '@/components/layout/BrandMark.vue'
import { useI18n } from 'vue-i18n'
import { SunIcon, MoonIcon, LogOut as LogOutIcon } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AdminSidebarNav from '@/components/admin/AdminSidebarNav.vue'
import { useAppShell } from '@/composables/useAppShell'
import { useAuthStore } from '@/stores/auth'
import { provide } from 'vue'
import { PAGE_TITLE_HOST, PAGE_TITLE_HOST_ID } from '@/components/layout/pageTitleHost'

provide(PAGE_TITLE_HOST, true)

const { t } = useI18n()
const { isDark, toggleTheme } = useAppShell()
const auth = useAuthStore()

// No navigation here — AdminApp's de-auth watcher owns the redirect + the
// deferred profile teardown (same contract as the console's UserMenu).
const handleLogout = async () => {
  await auth.logout()
}
</script>
